import { addDays, dayLabel, parseDay } from "./date";
import { dayShort } from "./foodView";
import { MEAL_LABEL, entriesFor, planFor, templatesOf, totalsOf } from "./intake";
import {
  answersFast,
  checkWords,
  dayFood,
  daySkeleton,
  fastFood,
  groupOf,
  isGenerated,
  mealReason,
  servingText,
  treatDays,
  weekShort,
  type CheckWords,
  type DaySkeleton,
  type PlanSlot,
  type PlanTargets,
} from "./planner";
import type { FoodEntry, FoodItem, KayaState, MealSlot } from "./types";

/**
 * The plan's screens in words and figures (FoodPlanGenerated, FoodDayPlanned, FoodDayFast,
 * FoodDayTwoMeals, FoodPlannedDay), kept out of the pages so the words are tested with the
 * numbers. (ADR-043)
 */

const n = (v: number) => Math.round(v).toLocaleString();

/** The template a planned item was placed from — `t:<template>:<item>:<day>` — or null. */
export function templateIdOf(id: string): string | null {
  const m = /^t:([^:]+):/.exec(id);
  return m ? m[1] : null;
}

export type DayTag = "generated" | "yours" | "fast" | null;

/** A fast day's name in the words the person used: "Ekadashi", "Tuesday fast", or "Fast day". */
export function fastName(S: KayaState, ds: string): string {
  const fasts = S.profile.eating?.fasts ?? [];
  if (answersFast(S, ds)) {
    const day = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][parseDay(ds).getDay()];
    return `${day} fast`;
  }
  const named = fasts.filter((f) => ["ekadashi", "navratri", "ramadan"].includes(f));
  if (named.length === 1) return named[0] === "ekadashi" ? "Ekadashi" : named[0] === "navratri" ? "Navratri" : "Ramadan";
  return "Fast day";
}

export interface WeekRow {
  ds: string;
  /** "Wed 23 · today" */
  label: string;
  kcal: number | null;
  protein: number | null;
  tag: DayTag;
}

export interface WeekView {
  rows: WeekRow[];
  /** "Planned, Thu 24 – Tue 29" */
  heroLabel: string;
  avgKcal: number | null;
  avgProtein: number | null;
  /** days the average counts */
  counted: number;
  /** true when any day of the week carries a generated item */
  generated: boolean;
  /** today's log is in the week, so the average starts tomorrow */
  todayLogged: boolean;
  /** the weekly shortfall line, or null */
  short: string | null;
}

/**
 * The seven days from today: each day's planned figures (today's is what was eaten plus what is
 * still planned), whose they are, and the average of the days still ahead. Today is left out of
 * the average once anything is logged — half a day eaten and half planned is neither.
 */
export function weekView(
  S: KayaState,
  today: string,
  T: PlanTargets | null,
  opts: { marked?: (ds: string) => boolean; lookup?: (code: string) => FoodItem | null } = {}
): WeekView {
  const rows: WeekRow[] = [];
  const food: FoodEntry[][] = [];
  let generated = false;
  for (let i = 0; i < 7; i++) {
    const ds = addDays(today, i);
    const items = dayFood(S, ds, today);
    const plan = planFor(S, ds);
    const t = totalsOf(items);
    const fast = answersFast(S, ds) || !!opts.marked?.(ds);
    const gen = plan.some((e) => isGenerated(e.id));
    generated ||= gen;
    rows.push({
      ds,
      label: `${dayShort(ds)}${ds === today ? " · today" : ""}`,
      kcal: items.length ? t.kcal : null,
      protein: items.length ? Math.round(t.p) : null,
      tag: fast ? "fast" : gen ? "generated" : plan.length ? "yours" : null,
    });
    food.push(items);
  }
  const todayLogged = (S.logs[today]?.food ?? []).some((e) => !(S.logs[today]?.foodOff && e.id in (S.logs[today]?.foodOff ?? {})));
  const from = todayLogged ? 1 : 0;
  const ahead = rows.slice(from).filter((r) => r.kcal !== null);
  const avg = (f: (r: WeekRow) => number) => (ahead.length ? Math.round(ahead.reduce((a, r) => a + f(r), 0) / ahead.length) : null);
  const first = rows[from];
  const last = rows[rows.length - 1];
  const short = T ? weekShort(food.slice(from), T, opts.lookup) : null;
  return {
    rows,
    heroLabel: first ? `Planned, ${dayShort(first.ds)} – ${dayShort(last.ds)}` : "Planned, next 7 days",
    avgKcal: avg((r) => r.kcal ?? 0),
    avgProtein: avg((r) => r.protein ?? 0),
    counted: ahead.length,
    generated,
    todayLogged,
    short: short?.line ?? null,
  };
}

/* ---------- one planned day ---------- */

export interface PlanRow {
  entry: FoodEntry;
  /** "1 katori · 18 g protein" */
  sub: string;
  generated: boolean;
  /** the template's name when the person placed it from one */
  template: string | null;
}

export interface PlanMeal {
  slot: PlanSlot;
  /** "Lunch · 620 of ~630 kcal" */
  heading: string;
  /** "34 g protein", "13 g protein · light", "13 g protein · fruit and curd" */
  sub: string;
  generated: boolean;
  rows: PlanRow[];
  /** the footer's one-line reason, for a meal the planner made */
  reason: string | null;
  /** under the rows, for a meal the person placed: "You placed this from Office day; a plan never replaces it." */
  note: string | null;
}

export interface PlanDayView {
  ds: string;
  skeleton: DaySkeleton;
  fast: boolean;
  /** "Ekadashi" on a fast day */
  fastName: string | null;
  /** "1,800 kcal · 118 g protein planned · budget ~1,810" */
  sub: string;
  meals: PlanMeal[];
  check: CheckWords | null;
}

export function planDayView(
  S: KayaState,
  ds: string,
  today: string,
  T: PlanTargets | null,
  opts: { fast?: boolean; lookup?: (code: string) => FoodItem | null; clock?: Date } = {}
): PlanDayView {
  const fast = !!opts.fast || answersFast(S, ds);
  const budget = T?.kcal ?? 0;
  const skeleton = daySkeleton(S, ds, budget, { fast, today: opts.clock });
  const items = planFor(S, ds);
  const tplName = new Map(templatesOf(S).map((t) => [t.id, t.name]));
  const meals: PlanMeal[] = [];
  const seen = new Set<MealSlot>();
  for (const slot of skeleton.slots) {
    seen.add(slot.meal);
    const mine = items.filter((e) => e.meal === slot.meal);
    meals.push(mealView(S, slot, mine, skeleton, tplName, opts.clock));
  }
  // A meal the skeleton does not have (a snack placed on a two-meal day) still shows.
  for (const m of ["breakfast", "lunch", "snack", "dinner"] as MealSlot[]) {
    if (seen.has(m)) continue;
    const mine = items.filter((e) => e.meal === m);
    if (!mine.length) continue;
    meals.push(mealView(S, { meal: m, label: MEAL_LABEL[m], share: 0, roles: [] }, mine, skeleton, tplName, opts.clock));
  }
  const t = totalsOf(items);
  const est = fast ? " est." : "";
  const name = fast ? fastName(S, ds) : null;
  const sub = items.length
    ? `${name ? `${name} · ` : ""}${n(t.kcal)} kcal · ${Math.round(t.p)} g protein planned · budget ${T ? `~${n(T.kcal)}${est}` : "–"}`
    : "Nothing planned for this day";
  return {
    ds,
    skeleton,
    fast,
    fastName: name,
    sub,
    meals,
    check: T && items.length ? checkWords(items, skeleton, T, { lookup: opts.lookup }) : null,
  };
}

function mealView(
  S: KayaState,
  slot: PlanSlot,
  items: FoodEntry[],
  skeleton: DaySkeleton,
  tplName: Map<string, string>,
  clock?: Date
): PlanMeal {
  const t = totalsOf(items);
  const generated = items.some((e) => isGenerated(e.id));
  const heading = slot.share > 0 ? `${slot.label} · ${n(t.kcal)} of ~${n(slot.share)} kcal` : `${slot.label} · ${n(t.kcal)} kcal`;
  const extra = slot.light ? " · light" : skeleton.fast && slot.meal === "breakfast" ? " · fruit and curd" : "";
  const rows: PlanRow[] = items.map((e) => {
    const tid = templateIdOf(e.id);
    return {
      entry: e,
      sub: `${servingText(e.qty, e.unit)} · ${Math.round(e.p)} g protein`,
      generated: isGenerated(e.id),
      template: tid ? (tplName.get(tid) ?? null) : null,
    };
  });
  const own = rows.filter((r) => !r.generated);
  const fromTpl = own.find((r) => r.template)?.template;
  const note = !generated && own.length
    ? fromTpl
      ? `You placed ${own.length === 1 ? "this" : "these"} from ${fromTpl}; a plan never replaces ${own.length === 1 ? "it" : "them"}.`
      : `You placed ${own.length === 1 ? "this" : "these"}; a plan never replaces ${own.length === 1 ? "it" : "them"}.`
    : null;
  return {
    slot,
    heading,
    sub: `${Math.round(t.p)} g protein${extra}`,
    generated,
    rows,
    reason: generated || slot.budgetOnly ? mealReason(S, slot, items, skeleton, clock) : null,
    note,
  };
}

/* ---------- copy and save ---------- */

/**
 * Why "Copy yesterday's food into this day" is off on a fast day, or null: "Fri 25 has grains —
 * not for Ekadashi". Only foods that could not be on a vrat plate stop it.
 */
export function copyBlocked(
  from: FoodEntry[],
  fromDs: string,
  fast: boolean,
  name: string,
  lookup: (code: string) => FoodItem | null
): string | null {
  if (!fast || !from.length) return null;
  const bad = from.filter((e) => {
    const row = e.code ? lookup(e.code) : null;
    return !fastFood(row ?? { i: e.code ?? "", n: e.name, u: e.unit, g: 1, k: 0, p: 0, c: 0, f: 0, src: "c" });
  });
  if (!bad.length) return null;
  const grains = bad.some((e) => groupOf({ n: e.name, gr: e.code ? lookup(e.code)?.gr : undefined }) === "grain");
  return `${dayShort(fromDs)} has ${grains ? "grains" : "foods that are not for a fast"} — not for ${name}`;
}

/* ---------- the Food tab ---------- */

/** Each meal's share of a day's budget, for the Food tab's headings ("Lunch · 558 of ~630"). */
export function mealShares(S: KayaState, ds: string, budget: number, fast = false, clock?: Date): Partial<Record<MealSlot, number>> {
  const out: Partial<Record<MealSlot, number>> = {};
  for (const s of daySkeleton(S, ds, budget, { fast, today: clock }).slots) out[s.meal] = (out[s.meal] ?? 0) + s.share;
  return out;
}

/** The Food tab's plan row: "This week's plan · 7 of 7 days" once the planner has run. */
export function planRowValue(S: KayaState, today: string): { generated: boolean; planned: number } {
  let planned = 0;
  let generated = false;
  for (let i = 0; i < 7; i++) {
    const items = planFor(S, addDays(today, i));
    if (items.length) planned++;
    if (items.some((e) => isGenerated(e.id))) generated = true;
  }
  return { generated, planned };
}

const CRAVE: Record<string, string> = { afternoon: "Afternoons", evening: "Evenings", night: "Nights" };
const TRIGGER_WORD: Record<string, string> = {
  sweets: "sweets",
  chocolate: "sweets",
  bakery: "sweets",
  fried: "fried snacks",
  chips: "fried snacks",
};

/**
 * The line that says a planned treat is on today (FoodPlannedDay): "Evenings are when sweets call —
 * one piece of barfi is planned for this evening's snack." with its evidence. Null when today's
 * plan has none.
 */
export function treatLine(
  S: KayaState,
  ds: string,
  lookup: (code: string) => FoodItem | null
): { text: string; basedOn: string } | null {
  const a = S.profile.eating ?? {};
  const days = treatDays(a.triggerMode);
  if (!days.length) return null;
  const treat = planFor(S, ds).find((e) => {
    if (!isGenerated(e.id)) return false;
    const g = groupOf({ n: e.name, gr: e.code ? lookup(e.code)?.gr : undefined });
    return g === "sweet" || g === "fried";
  });
  if (!treat) return null;
  const trigger = TRIGGER_WORD[(a.triggers ?? [])[0] ?? "sweets"] ?? "sweets";
  const when = CRAVE[a.cravingTime ?? ""];
  const unit = treat.unit.replace(/\s*\(.*\)$/, "");
  const what = `${treat.qty === 1 ? "one" : servingText(treat.qty, unit).split(" ")[0]} ${unit} of ${treat.name.replace(/\s*\(.*$/, "").toLowerCase()}`;
  const slot = treat.meal === "snack" ? "this evening’s snack" : `${MEAL_LABEL[treat.meal].toLowerCase()}`;
  const text = when ? `${when} are when ${trigger} call — ${what} is planned for ${slot}.` : `A small portion — ${what} — is planned for ${slot}.`;
  return {
    text,
    basedOn: `based on your eating answers · ${days.length} small ${trigger === "sweets" ? "sweets" : "treats"} planned this week`,
  };
}

/**
 * What "Copy yesterday's food into this day" copies: what was eaten that day when anything was,
 * else what was planned for it.
 */
export function copySource(S: KayaState, fromDs: string, today: string): { items: FoodEntry[]; logged: boolean } {
  const eaten = fromDs <= today ? entriesFor(S, fromDs) : [];
  return eaten.length ? { items: eaten, logged: true } : { items: planFor(S, fromDs), logged: false };
}

/** "Wed 23 Sep · 1,240 kcal logged so far" — the Copy yesterday row's sub-line. */
export function copyFromLine(src: { items: FoodEntry[]; logged: boolean }, fromDs: string, today: string): string {
  const t = totalsOf(src.items);
  if (!src.items.length) return `${dayLabel(fromDs)} · nothing yet`;
  const how = src.logged ? (fromDs === today ? "logged so far" : "logged") : "planned";
  return `${dayLabel(fromDs)} · ${n(t.kcal)} kcal ${how}`;
}
